<template>
  <div>
    <div @click="openModal()">
      <slot name="buttonTop" />
    </div>
    <ModalHeadlessUI ref="modal">
      <template #default>
        <h1 class="font-bold mb-4 text-3xl text-center">
          Edit Task
        </h1>
        <form id="taskForm" name="taskForm" @submit.prevent="updateTask">
          <label class="form-label">Set Date and Time:</label>
          <Datepicker v-model="taskForm.trigger_time" class="mb-4 pr-6 w-full" :is-24="false" />
          <div v-if="taskForm.errors.trigger_time" class="form-error pb-4 w-full">{{ taskForm.errors.trigger_time }}</div>
          <select-input v-model="taskForm.type" :error="taskForm.errors.type" class="pr-6 pb-4 w-full" label="Task Type">
            <option value="reminder">Reminder</option>
            <option value="email">Email</option>
            <option value="text">SMS</option>
            <option value="voicemail">Voicemail</option>
            <option value="tag">Tag</option>
            <option value="status">Status</option>
          </select-input>
          <text-input v-if="!hideEmail" v-model="taskForm.subject" :error="taskForm.errors.subject" class="pr-6 pb-4 w-full" label="Email Subject" />
          <div v-if="hideForVoicemail && hideForTag && hideForStatus">
            <label class="form-label">Message:</label>
            <TaskEditor v-model="taskForm.message" :error="taskForm.errors.message" />
          </div>         
          <text-input v-if="!hideForTag" v-model="taskForm.message" :error="taskForm.errors.message" class="pr-6 pb-4 w-full" label="Enter Tag" />
          <select-input v-if="!hideForVoicemail" v-model="taskForm.message" :error="taskForm.errors.type" class="pr-6 pb-4 w-full" label="Voicemail Message" @change="handleVoicemailChange">
              <option v-for="voicemail in voicemails" :key="voicemail.id" :value="voicemail.name">{{ voicemail.display }}</option>
          </select-input>
          <div v-if="taskForm.errors.message" class="form-error">{{ taskForm.errors.message }}</div>
          <StatusDropdown
                v-if="!hideForStatus" 
                class="pr-6 pb-4 w-full"
                v-model="taskForm.message"
                label="Lead Status"
                :error="taskForm.errors.message"
                @change="handleStatusChange"
                @statusText="handleStatusText"
              />
          <div class="w-full lg:w-auto mb-4 mt-4 text-center">
              <div class="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in">
                <input id="stop_on_reply" v-model="taskForm.stop_on_reply" :true-value="1" :false-value="0" name="stop_on_reply" type="checkbox" class="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 appearance-none cursor-pointer" />
                <label for="stop_on_reply" class="toggle-label block overflow-hidden h-6 rounded-full bg-gray-300 cursor-pointer" />
              </div>
              <label for="automations">Stop on Reply</label>
          </div>
          <div v-if="hideForTag && hideForVoicemail && hideForStatus" class="pb-4 w-full">
            <attachment-uploader :initial-attachment-url="taskForm.attachment" message-type="email" @update:attachmentUrl="updateAttachmentUrl" @update:attachmentName="updateAttachmentName"/>
          </div>
          <div class="flex justify-center items-center mt-5">
            <div>
              <loading-button :loading="taskForm.processing" class="btn-indigo ml-auto" type="submit">Update Task</loading-button>
            </div>
          </div>
        </form>
      </template>
    </ModalHeadlessUI>
  </div>
</template>

<script>
import ModalHeadlessUI from '@/Shared/ModalHeadlessUI'
import TaskEditor from '@/Shared/TaskEditor'
import SelectInput from '@/Shared/SelectInput'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'
import Datepicker from '@vuepic/vue-datepicker'
import AttachmentUploader from '@/Shared/AttachmentUploader'
import '@vuepic/vue-datepicker/dist/main.css'
import StatusDropdown from '@/Shared/StatusDropdown'
import { router } from '@inertiajs/vue3'

export default {
  components: {
    ModalHeadlessUI,
    Datepicker,
    TaskEditor,
    SelectInput,
    TextInput,
    LoadingButton,
    AttachmentUploader,
    StatusDropdown,
  },
  props: {
    task:{
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      taskForm: router.form({
        trigger_time: this.task.trigger_time,
        type: this.task.type,
        subject: this.task.subject,
        message: this.task.message,
        attachment: this.task.attachment,
        attachmentName: this.task.attachmentName,
        stop_on_reply: this.task.stop_on_reply,
      }),
      attachmentError: '',
      voicemails: [],
    }
  },
  computed:{
    hideEmail() {
      return this.taskForm.type == 'email' ?  false : true
    },    
    hideForVoicemail() {
      return this.taskForm.type == 'voicemail' ?  false : true
    },
    hideForTag() {
      return this.taskForm.type == 'tag' ?  false : true
    },
    hideForStatus() {
      return this.taskForm.type == 'status' ?  false : true
    },
  },
  mounted() {
    this.fetchVoicemailRecordings()
  },
  methods:{
    openModal() {
      this.$refs.modal.openModal()
    },
    closeModal() {
      this.$refs.modal.closeModal()
    },
    handleStatusChange(newValue) {
      this.taskForm.message = newValue
    },
    handleStatusText(newValue) {
      this.taskForm.subject = newValue
    },
    updateMessage: function(messageValue){
      this.taskForm.message=messageValue
    },    
    removeAttachment() {
      this.taskForm.attachment=null
      this.taskForm.attachmentName=null
    },
    isImage(url) {
      // Regular expression to match common image extensions
      const imageExtensions = /\.(jpg|jpeg|png|gif)$/i;
      return imageExtensions.test(url);
    },
    updateAttachmentUrl(url) {
      this.taskForm.attachment = url
    },
    updateAttachmentName(name) {
      this.taskForm.attachmentName = name
    },
    async fetchVoicemailRecordings() {
      try {
        const response = await fetch('/voicemail/getRecordings', {
          headers: {
            'Accept': 'application/json'
          }
        });
        const data = await response.json();
        // Transform the recordings data to match the format expected by the select input
        this.voicemails = data.recordings.map(recording => ({
          id: recording.id,
          name: recording.file_path,
          display: recording.name
        }));
      } catch (error) {
        console.error('Error fetching voicemail recordings:', error);
      }
    },
    handleVoicemailChange(event) {
      // Find the selected voicemail
      const selectedVoicemail = this.voicemails.find(vm => vm.name === event.target.value);
      if (selectedVoicemail) {
        // Store the display name in the subject field
        this.taskForm.subject = selectedVoicemail.display;
      }
    },
    updateTask() {
      if (this.taskForm.type === 'voicemail') {
        // Check if the selected voicemail exists in the recordings list
        const voicemailNames = this.voicemails.map(voicemail => voicemail.name);
        if (!voicemailNames.includes(this.taskForm.message)) {
          alert('Invalid voicemail selection');
          return;
        }
      }
      
      var postTo = '/tasks/'+this.task.id
      router.put(postTo, this.taskForm, {
        onSuccess: () => {
          this.closeModal()
        },
        only: ['tasks'],
        preserveScroll: true,
      })
    },
  },
}
</script>
